import React from "react";
import "./StarRating.css";

// --- STAR RATING (animations from Uiverse.io by elijahgummer, adapted) ---
// The original snippet drives "fill" via the classic hidden-radio +
// `:checked ~ label` CSS sibling trick, which needs the stars in reversed
// DOM order (5,4,3,2,1) with a row-reverse layout to read correctly, and
// breaks if anything else ever needs to read "the current rating" (this
// app already tracks responseRating/qualityRating in React state and
// reads them elsewhere). So the fill logic here is done the ordinary way
// — plain React state, `star <= value` — and only the VISUAL language of
// the original (hover scale+pulse, particle-burst dots, glow/shimmer,
// the selected star's own persistent pulse) is carried over via CSS
// classes driven by that state.

export interface StarRatingProps {
  /** Current rating, 0-max. */
  value: number;
  onChange: (value: number) => void;
  max?: number;
  /** Star size in px — maps to the original's `font-size: 30px`. */
  size?: number;
  readOnly?: boolean;
  ariaLabel?: string;
}

const STAR_PATH =
  "M12 2.5l2.95 6.28 6.93.63-5.24 4.62 1.57 6.82L12 17.02l-6.21 3.83 1.57-6.82-5.24-4.62 6.93-.63L12 2.5z";

export default function StarRating({
  value,
  onChange,
  max = 5,
  size = 30,
  readOnly = false,
  ariaLabel = "Rating",
}: StarRatingProps) {
  const [hoverValue, setHoverValue] = React.useState<number | null>(null);
  const displayValue = hoverValue ?? value;

  return (
    <div
      className="star-rating"
      role="radiogroup"
      aria-label={ariaLabel}
      style={{ ["--star-size" as any]: `${size}px` }}
      onMouseLeave={() => !readOnly && setHoverValue(null)}
    >
      {Array.from({ length: max }, (_, i) => i + 1).map((star) => {
        const isFilled = star <= displayValue;
        // Only the exact star matching the committed rating gets the
        // persistent pulse — mirrors the original's `input:checked +
        // label` targeting just the one truly-checked radio, not every
        // filled star before it.
        const isExactSelected = star === value && hoverValue == null;

        return (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={star === value}
            aria-label={`${star} star${star > 1 ? "s" : ""}`}
            disabled={readOnly}
            className={`star-rating-item${isFilled ? " is-filled" : ""}${
              isExactSelected ? " is-exact-selected" : ""
            }`}
            onMouseEnter={() => !readOnly && setHoverValue(star)}
            onClick={() => !readOnly && onChange(star)}
          >
            <svg viewBox="0 0 24 24" className="star-rating-svg">
              <path d={STAR_PATH} />
            </svg>
          </button>
        );
      })}
    </div>
  );
}
